import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { FaTags } from 'react-icons/fa';
import { IoSearchSharp } from 'react-icons/io5';
import { useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { hideDialog, showDialog } from '../../../core/actions';
import DataTable from '../../../core/components/DataTable';
import TableActionButtonsCell from '../../../core/components/TableActionButtonsCell';
import { selectCoreSiteId } from '../../settings/reducer';
import { selectMultiSiteSelection, selectSitesBasic } from '../../site-selection/reducer';
import * as actions from '../../inventory/actions';
import { getApiStatus } from '../../../core/utils';
import InventoryDetail from '../../inventory/containers/InventoryDetail';
import UserGroupPermissions, {
  addPermissionRefs,
  viewPermissionRefs,
} from '../../../core/components/UserGroupPermissions';

import {
  selectInventoryTypes,
  selectFromDate,
  selectUntilDate,
  selectTypeFilter,
  selectEssentialPage,
  selectEssentialPageSize,
  selectEssentialParts,
  selectEssentialTotal,
  selectEssentialPartsInEdit,
} from '../../inventory/reducer';
import { Drawer, DrawerContainer } from '../../../core/components/Drawer';
import ButtonWithLoader from '../../../core/components/ButtonWithLoader';
import CsvUploader from '../../../core/components/CSVUploader';
import Loader from '../../../core/components/Loader';
import { getSiteLabel } from '../../site-selection/helpers';
import { FilterConfig, FilterPlacement, PageFilters } from '../../../core/components/PageFilters';

const toFilterDate = (value: moment.MomentInput): Date | null => {
  if (!value) return null;
  const date = moment(value);
  return date.isValid() ? date.toDate() : null;
};

export const EssentialParts = ({
  navigationCount = 0,
}: {
  navigationCount?: number;
}): JSX.Element => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const essentialParts = useSelector(selectEssentialParts);
  const inventoryTypes = useSelector(selectInventoryTypes);
  const filter = useSelector(selectTypeFilter);
  const total = useSelector(selectEssentialTotal);
  const page = useSelector(selectEssentialPage);
  const sites = useSelector(selectSitesBasic);
  const pageSize = useSelector(selectEssentialPageSize);
  const siteId = useSelector(selectCoreSiteId);
  const multiSites = useSelector(selectMultiSiteSelection);
  const [inventoryExpanded, setExpanded] = useState(false);
  const [highlightedRow, setHighlightedRow] = useState(0);
  const essentialPartsInEdit = useSelector(selectEssentialPartsInEdit);
  const inventoryAdded = useSelector((state) => getApiStatus(state, 'ADD_INVENTORY_ITEM'));
  const inventoryEdited = useSelector((state) => getApiStatus(state, 'SAVE_INVENTORY_ITEM'));
  const inventoryDeleted = useSelector((state) => getApiStatus(state, 'DELETE_INVENTORY_ITEM'));
  const inventoryFetching = useSelector((state) => getApiStatus(state, 'FETCH_INVENTORY'));
  const batchInventoryAdded = useSelector((state) =>
    getApiStatus(state, 'BATCH_SAVE_INVENTORY_ITEM_SUCCESS')
  );

  //Search variables
  const fromDate = useSelector(selectFromDate);
  const untilDate = useSelector(selectUntilDate);
  const [searchText, setSearchText] = useState('');

  const getInventoryQuery = (pageNumber = 0) => ({
    page: pageNumber,
    inventoryType: filter,
    fromDate,
    untilDate,
    searchText,
    priority: true,
  });

  useEffect(() => {
    updateInventory();
  }, [
    filter,
    siteId,
    multiSites,
    fromDate,
    untilDate,
    searchText,
    inventoryAdded,
    inventoryEdited,
    inventoryDeleted,
    batchInventoryAdded,
    navigationCount,
  ]);

  useEffect(() => {
    dispatch(actions.fetchInventoryTypes());
  }, [siteId]);

  useEffect(() => {
    setExpanded(false);
    setHighlightedRow(0);
  }, [filter, searchText]);

  const getInventoryPage = (selectedPage: any) => {
    dispatch(actions.fetchInventory(getInventoryQuery(selectedPage)));
  };

  const getPaginationInfo = () => {
    return {
      total: total,
      page: page,
      pageSize: pageSize,
      goToPage: getInventoryPage,
    };
  };

  const updateInventory = () => {
    dispatch(actions.fetchInventory(getInventoryQuery(0)));
  };

  const submitBatchInventory = (data: any) => {
    data = data.map((d: any) => {
      return {
        ...d,
        groupId: parseInt(d.groupId),
        siteId: parseInt(d.siteId),
        inventoryTypeId: parseInt(d.inventoryTypeId),
        preferredStockLevel: parseInt(d.preferredStockLevel),
        priority: d.priority === 'TRUE' ? true : false,
      };
    });

    dispatch(actions.batchAddInventoryItems(data));
  };

  const handleUploadCSV = () => {
    const dialogProps = {
      onSubmit: (data: any) => submitBatchInventory(data),
      title: intl.formatMessage({ id: 'uploadCSV' }),
      intl: intl,
      close: () => dispatch(hideDialog()),
      className: 'enhanced-dialog',
      showFooter: false,
      defaultModal: CsvUploader,
    };

    dispatch(showDialog('UPSERT_INVENTORY', dialogProps));
  };

  const addInventory = () => {
    const dialogProps = {
      onSubmit: (item: any) => dispatch(actions.addInventoryItem(item)),
      isNew: true,
      title: intl.formatMessage({ id: 'addEssentialPart' }),
      intl: intl,
      isEssentialPart: true,
    };
    dispatch(showDialog('UPSERT_INVENTORY', dialogProps));
  };

  const editInventory = (event: any, { _id }: any) => {
    dispatch(actions.getInventoryItem(_id));
    const dialogProps = {
      title: intl.formatMessage({ id: 'editInventoryItem' }),
      onSubmit: (item: any) => dispatch(actions.saveInventoryItem(item)),
      isNew: false,
      intl: intl,
    };
    dispatch(showDialog('UPSERT_INVENTORY', dialogProps));
  };

  const deleteInventory = (event: any, { _id, name }: any) => {
    const dialogProps = {
      onReady: () => {
        dispatch(actions.deleteInventoryItem({ id: _id }));
      },
      text: intl.formatMessage({ id: 'inventoryItemArchiveConfirmation' }, { name }),
      title: intl.formatMessage({ id: 'archiveInventoryItem' }),
    };
    dispatch(showDialog('CONFIRM_DIALOG', dialogProps));
  };

  const getRowClickInfo = () => ({
    callback: (data: { _id: React.SetStateAction<number> }) => {
      setExpanded(true);
      setHighlightedRow(data._id);
      dispatch(actions.getInventoryItem(data._id));
    },
  });

  const initializeDatatable = (initialInventory: any[]) => {
    const data = initialInventory
      .filter((i: any) => i.priority)
      .map(
        (i: {
          id: any;
          createdAt: any;
          number: any;
          name: any;
          description: any;
          installedAt: moment.MomentInput;
          preferredStockLevel: any;
          unitOfMeasure: any;
          priority: any;
          siteId: any;
        }) => {
          return {
            _id: i.id,
            name: i.name,
            partNumber: i.number,
            description: i.description,
            siteId: getSiteLabel(sites, i.siteId),
            createdAt: i.createdAt
              ? moment(i.createdAt).format('MM/DD/YYYY')
              : intl.formatMessage({ id: 'notAvailable' }),
            preferredStockLevel: i.preferredStockLevel,
            unit: i?.unitOfMeasure || '',
          };
        }
      );

    const headers = [
      { name: intl.formatMessage({ id: 'name' }) },
      { name: intl.formatMessage({ id: 'partNumber' }) },
      { name: intl.formatMessage({ id: 'description' }) },
      { name: intl.formatMessage({ id: 'site' }) },
      { name: intl.formatMessage({ id: 'createdAt' }) },
      { name: intl.formatMessage({ id: 'requiredStock' }) },
      { name: intl.formatMessage({ id: 'unit' }) },
      {
        name: intl.formatMessage({ id: 'action' }),
        cell: {
          component: TableActionButtonsCell,
          mixIns: {
            buttons: [
              { icon: 'icon-trash', buttonAction: deleteInventory },
              { icon: 'icon-edit', buttonAction: editInventory },
            ],
          },
        },
      },
    ];

    return {
      data,
      rowClickInfo: getRowClickInfo(),
      headers,
    };
  };

  const drawerControlsConfig = () => {
    return {
      closeFunction: () => {
        setExpanded(false);
        dispatch(actions.deleteInventoryInEdit());
      },
    };
  };

  const data = essentialParts;
  const isLoading =
    inventoryFetching ||
    inventoryAdded ||
    inventoryDeleted ||
    inventoryEdited ||
    batchInventoryAdded;

  const filterConfigs: FilterConfig[] = useMemo(
    () => [
      {
        id: 'dateRange',
        type: 'dateRange',
        name: intl.formatMessage({ id: 'date' }),
        placement: FilterPlacement.Chiclet,
        defaultVisible: true,
        icon: <i className="uil uil-calendar-alt" />,
        startDate: toFilterDate(fromDate),
        endDate: toFilterDate(untilDate),
        native: true,
        onChange: ([startDate, endDate]: [Date | null, Date | null]) => {
          dispatch(actions.setFromDate(startDate ? moment(startDate) : fromDate));
          dispatch(actions.setUntilDate(endDate ? moment(endDate) : untilDate));
        },
      },
      {
        id: 'searchText',
        type: 'search',
        name: intl.formatMessage({ id: 'search', defaultMessage: 'Search' }),
        placement: FilterPlacement.Chiclet,
        defaultVisible: true,
        icon: <IoSearchSharp />,
        debounceMs: 0,
        searchTerm: searchText,
        onChange: (value: string | null) => setSearchText(value || ''),
      },
      {
        id: 'inventoryType',
        type: 'select',
        name: intl.formatMessage({ id: 'type', defaultMessage: 'Type' }),
        placement: FilterPlacement.Chiclet,
        defaultVisible: true,
        multiSelect: false,
        icon: <FaTags />,
        options: [
          {
            value: 'All',
            text: intl.formatMessage({ id: 'all', defaultMessage: 'All' }),
            selected: Number(filter) === 0,
          },
          ...inventoryTypes.map((option: any) => ({
            value: String(option.id),
            text: option.name,
            selected: Number(filter) === Number(option.id),
          })),
        ],
        onChange: (values: string[]) => {
          const selectedValue = values[0] || 'All';
          dispatch(actions.setTypeFilter(selectedValue === 'All' ? 0 : Number(selectedValue)));
        },
      },
    ],
    [dispatch, filter, fromDate, intl, inventoryTypes, searchText, untilDate]
  );

  return (
    <div data-testid="essentialPartsContainer" className="essential-parts-container">
      <header className="filters-actions-container essential-parts-toolbar mb-3">
        <PageFilters filters={filterConfigs} maxChicletFilters={3} />
        <div className="page-action-buttons">
          <UserGroupPermissions itemRef={viewPermissionRefs.admin}>
            <ButtonWithLoader style="button add-task mr-1" handleClick={() => handleUploadCSV()}>
              <>
                <span data-testid="addEssentialPartButton" className="add-button-text">
                  {intl.formatMessage({ id: 'uploadCSV' })}
                </span>
              </>
            </ButtonWithLoader>
          </UserGroupPermissions>
          <UserGroupPermissions itemRef={addPermissionRefs.inventory}>
            <ButtonWithLoader style="button add-task" handleClick={() => addInventory()}>
              <>
                <i className="icon icon-plus"></i>
                <span data-testid="addEssentialPartButton" className="add-button-text">
                  {intl.formatMessage({ id: 'addEssentialPart' })}
                </span>
              </>
            </ButtonWithLoader>
          </UserGroupPermissions>
        </div>
      </header>
      <DrawerContainer>
        <div>
          {!isLoading ? (
            <div className="search-results">
              <DataTable
                paginationInfo={getPaginationInfo()}
                highlightedRow={highlightedRow}
                scrolling
                {...initializeDatatable(data)}
              />
            </div>
          ) : (
            <Loader />
          )}
        </div>
        <Drawer expanded={inventoryExpanded}>
          <div className="inventory-details-wrapper">
            {essentialPartsInEdit && (
              <InventoryDetail isPage={false} drawerControls={{ ...drawerControlsConfig() }} />
            )}
          </div>
        </Drawer>
      </DrawerContainer>
    </div>
  );
};

export default EssentialParts;
